import { useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../utils/config';
import { useAuth } from '../../../context/AuthContext';

const BASE = `${API_BASE_URL}/finance`;

// Unwrap the {success, message, data} envelope -> data
const unwrap = (res) =>
  res && res.data && Object.prototype.hasOwnProperty.call(res.data, 'data')
    ? res.data.data
    : res.data;

/**
 * Authenticated Finans API client. Mirrors the app convention:
 * axios + getAuthHeaders() from AuthContext + API_BASE_URL from config.
 */
export function useFinanceApi() {
  const { getAuthHeaders, token } = useAuth();

  return useMemo(() => {
    const cfg = () => ({ headers: getAuthHeaders() });

    return {
      // Generic CRUD
      list: (resource, params = {}) =>
        axios.get(`${BASE}/records/${resource}`, { ...cfg(), params }).then(unwrap),
      create: (resource, payload) =>
        axios.post(`${BASE}/records/${resource}`, payload, cfg()).then((r) => r.data),
      update: (resource, id, payload) =>
        axios.put(`${BASE}/records/${resource}/${id}`, payload, cfg()).then((r) => r.data),
      remove: (resource, id) =>
        axios.delete(`${BASE}/records/${resource}/${id}`, cfg()).then((r) => r.data),

      // Shared
      lookups: () => axios.get(`${BASE}/lookups`, cfg()).then(unwrap),
      myPermissions: () => axios.get(`${BASE}/my-permissions`, cfg()).then(unwrap),

      // Arbitrary endpoints under /finance
      get: (path, params = {}) =>
        axios.get(`${BASE}${path}`, { ...cfg(), params }).then(unwrap),
      post: (path, body = {}) =>
        axios.post(`${BASE}${path}`, body, cfg()).then((r) => r.data),

      // Cari logo (multipart — let the browser set the boundary)
      uploadLogo: (cariId, file) => {
        const fd = new FormData();
        fd.append('file', file);
        return axios
          .post(`${BASE}/cari/${cariId}/logo`, fd, {
            headers: { Authorization: getAuthHeaders().Authorization },
          })
          .then((r) => r.data);
      },
    };
  }, [token]);
}

export const DOVIZ_OPTIONS = ['TRY', 'USD', 'EUR', 'GBP'].map((d) => ({ value: d, label: d }));

// lookups[entity] -> antd Select options. Optional filter (e.g. işlem türü by odeme).
export function entityOptions(lookups, entity, filterFn) {
  const list = (lookups && lookups[entity]) || [];
  const arr = filterFn ? list.filter(filterFn) : list;
  return arr.map((item) => {
    if (entity === 'firma') return { value: item.id, label: item.firma_adi };
    if (entity === 'hesaplar') return { value: item.id, label: item.label };
    if (entity === 'cari') return { value: item.id, label: item.kod ? `${item.ad} (${item.kod})` : item.ad };
    return { value: item.id, label: item.ad };
  });
}
